// client/src/pages/AffiliatesPending.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  doc,
  onSnapshot,
  setDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { firestore, storage } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { sanitize } from '../../utils/sanitize';
import { FaCheck, FaUserCircle } from 'react-icons/fa';
import { logoutSafely } from '../../utils/logoutSafely';
import LegalAccordion from '../shared/LegalAccordion';

const AffiliatesPending = () => {
  const [user, setUser] = useState(null);
  const [userDoc, setUserDoc] = useState(null); // Firestore users/{uid}
  // eslint-disable-next-line
  const [loadingUserDoc, setLoadingUserDoc] = useState(true);

  const navigate = useNavigate();

  // ===== Responsive (mobile dès 770px)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(typeof window !== 'undefined' && window.innerWidth <= 770);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ===== Auth watcher
  useEffect(() => {
    const auth = getAuth();
    const unsubAuth = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsubAuth();
  }, []);

  // ===== Live redirection si le rôle passe à "affiliates"
  useEffect(() => {
    if (!user) return;
    const refUser = doc(firestore, 'users', user.uid);
    const unsub = onSnapshot(refUser, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() || {};
      const role = sanitize(data.role || '');
      if (role === 'affiliates') {
        localStorage.setItem('userRole', 'affiliates');
        localStorage.setItem('userEmail', user.email || '');
        navigate('/affiliatesdashboard');
      }
    });
    return () => unsub();
  }, [user, navigate]);

  // ===== Charger / écouter le profil user (pour savoir si déjà soumis)
  useEffect(() => {
    if (!user) return;
    const uref = doc(firestore, 'users', user.uid);
    const unsub = onSnapshot(uref, (snap) => {
      setLoadingUserDoc(false);
      if (!snap.exists()) { setUserDoc(null); return; }
      setUserDoc(snap.data());
    }, () => setLoadingUserDoc(false));
    return () => unsub();
  }, [user]);

  // ====== États formulaire (étape 1 : infos) + (étape 2 : documents) + (étape 3 : note)
  const [step, setStep] = useState(1);

  // Étape 1 – Infos personnelles
  const [newPseudo, setNewPseudo] = useState('');
  const [newNom, setNewNom] = useState('');
  const [newPrenom, setNewPrenom] = useState('');
  const [newDateNaissance, setNewDateNaissance] = useState('');
  const [newTelephone, setNewTelephone] = useState('');
  const [newAdresse, setNewAdresse] = useState('');
  const [acceptedCGUV, setAcceptedCGUV] = useState(false);

  // Étape 2 – Fichiers
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  const [selectedKbisFile, setSelectedKbisFile] = useState(null);
  const [selectedRibFile, setSelectedRibFile] = useState(null);
  const [selectedContratFile, setSelectedContratFile] = useState(null);

  // Étape 3 – Note
  const [newNote, setNewNote] = useState('');

  // UI / états
  const [isSaving, setIsSaving] = useState(false);
  // eslint-disable-next-line
  const fileInputRef = useRef(null);

  // Aperçu local image (cleanup)
  useEffect(() => {
    if (!selectedPhotoFile) return;
    const url = URL.createObjectURL(selectedPhotoFile);
    return () => URL.revokeObjectURL(url);
  }, [selectedPhotoFile]);

  // ====== Styles
  const THEME_PRIMARY = 'var(--eb-brand)';

  const baseStyles = `
    .page {
      min-height: 100vh; min-height: 100dvh; display:flex; align-items:center; justify-content:center;
      background:
        radial-gradient(900px 500px at 15% -10%, rgba(39, 76, 116, 0.12), transparent 60%),
        var(--eb-bg);
      color: var(--eb-text); padding: 32px 16px;
    }
    .card {
      background: var(--eb-surface); border:1px solid var(--eb-border); border-radius: var(--eb-radius-xl);
      box-shadow: var(--eb-shadow-lg);
      width: 100%; max-width: 760px; padding: 32px; position: relative;
    }
    .title { font-size: 24px; font-weight: 700; letter-spacing: -0.02em; margin: 0 0 6px 0; color: var(--eb-text); }
    .sub { color: var(--eb-muted); margin: 0 0 24px 0; font-size: 14.5px; }
    .muted { color: var(--eb-muted); font-size: 14px; margin-top: 10px; }
    .btn {
      display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      min-height: 44px; padding: 0 20px;
      background: var(--eb-brand); color:#fff; border: 1px solid transparent; border-radius: var(--eb-radius-sm);
      font-weight: 600; font-size: 14.5px; cursor:pointer;
      transition: background-color .15s ease, border-color .15s ease, color .15s ease;
    }
    .btn:hover:not(:disabled) { background: var(--eb-brand-700); }
    .btn:disabled { opacity: .55; cursor: not-allowed; }
    .btn.secondary { background: var(--eb-success); }
    .btn.secondary:hover:not(:disabled) { filter: brightness(0.93); background: var(--eb-success); }
    .btn.ghost { background: var(--eb-surface); color: var(--eb-text-2); border-color: var(--eb-border-strong); }
    .btn.ghost:hover:not(:disabled) { background: var(--eb-surface-2); }
    .grid { display:grid; grid-template-columns: repeat(${isMobile ? 1 : 2}, minmax(0, 1fr)); gap: 14px; }
    .input, .textarea {
      width: 100%; min-height: 46px; padding: 0 14px; font-size: 15px;
      border-radius: var(--eb-radius-sm); border: 1px solid var(--eb-border-strong); background: var(--eb-surface);
      box-shadow: var(--eb-shadow-xs);
    }
    .textarea { min-height: 120px; padding: 12px 14px; resize: vertical; }
    .section { padding: 0; }
    .section-title { margin: 0 0 16px; font-size: 17px; font-weight: 650; color: var(--eb-text); text-align: left; }
    .field-label { font-size: 13.5px; font-weight: 600; color: var(--eb-text-2); margin-bottom: 6px; display: block; }
    .field-warn { color: var(--eb-warning); font-size: 13px; margin: 6px 0 0; }
    .field-err { color: var(--eb-danger); font-size: 13.5px; margin: 8px 0 0; font-weight: 500; }
    .steps { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin-bottom: 28px; }
    .chip-ok {
      display:flex; align-items:center; gap:8px;
      padding: 10px 12px; border-radius: var(--eb-radius-sm);
      background: var(--eb-surface-2); color: var(--eb-muted); border: 1px solid var(--eb-border);
      font-size: 13.5px; font-weight: 600;
    }
    .chip-ok.is-done { background: var(--eb-brand-50); color: var(--eb-brand); border-color: var(--eb-brand-100); }
    .actions-row { display: flex; gap: 10px; margin-top: 24px; justify-content: space-between; flex-wrap: wrap; }
    .actions-row--end { justify-content: flex-end; }
    .photo-pick { display: inline-flex; flex-direction: column; align-items: center; gap: 8px; }

    /* === Tiles de documents === */
    .file-tile {
      display:flex;
      flex-direction:column;
      align-items:flex-start;
      justify-content:space-between;
      gap:8px;
      min-height: 120px;
      border:1px dashed var(--eb-border-strong);
      border-radius: var(--eb-radius);
      background: var(--eb-surface-2);
      padding:14px;
      transition: background-color .15s ease, border-color .15s ease;
      cursor: pointer;
      overflow: hidden; /* empêche les débordements */
    }
    .file-tile:hover { border-color: var(--eb-brand-500); background: var(--eb-brand-50); }

    .file-tile.ok {
      border-style: solid;
      background: var(--eb-success-bg);
      border-color: #c6f0da;
    }

    .file-heading { font-weight: 600; font-size: 14.5px; color: var(--eb-text); }
    .file-sub { font-size: 13px; color: var(--eb-muted); }
    .file-footer {
      width: 100%;
      font-size: 13px; color: var(--eb-muted); margin-top: 6px; padding-top: 8px; border-top: 1px solid var(--eb-border);
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-height: 16px;
    }
    .file-tile.ok .file-footer { color: var(--eb-success); font-weight: 600; }
    @media (max-width: 600px) {
      .card { padding: 24px 18px; border-radius: var(--eb-radius-lg); }
      .page { align-items: flex-start; padding-top: 20px; }
      .steps { gap: 6px; }
      .chip-ok { padding: 8px; font-size: 12.5px; flex-direction: column; text-align: center; gap: 4px; }
      .actions-row .btn { flex: 1; }
    }
  `;

  // ====== Validation étape 1
  const isStep1Valid = useMemo(
    () =>
      newPseudo.trim() &&
      newNom.trim() &&
      newPrenom.trim() &&
      newDateNaissance.trim() &&
      newTelephone.trim() &&
      newAdresse.trim() &&
      acceptedCGUV &&
      !isSaving,
    [newPseudo, newNom, newPrenom, newDateNaissance, newTelephone, newAdresse, acceptedCGUV, isSaving]
  );

  // ====== Validation étape 2 (photo + 3 docs requis)
  const isStep2Valid = useMemo(
    () =>
      Boolean(selectedPhotoFile && selectedKbisFile && selectedRibFile && selectedContratFile) && !isSaving,
    [selectedPhotoFile, selectedKbisFile, selectedRibFile, selectedContratFile, isSaving]
  );

  // ====== Création doc "code promo/{uid}" (cohérence avec ton flow)
  const handleGeneratePromoRoot = async (uid, pseudo) => {
    try {
      const promoRef = doc(firestore, 'code promo', uid);
      await setDoc(promoRef, { uid, pseudo }, { merge: true });
    } catch (e) {
      console.error('Erreur création doc code promo:', e);
    }
  };

  // ====== Sauvegarde finale (infos + uploads + note + promo root)
  const handleSaveAll = async () => {
    if (!user?.uid) return;

    if (
      newPseudo.trim() &&
      newNom.trim() &&
      newPrenom.trim() &&
      newDateNaissance.trim() &&
      newTelephone.trim() &&
      newAdresse.trim() &&
      acceptedCGUV &&
      selectedPhotoFile &&
      selectedKbisFile &&
      selectedRibFile &&
      selectedContratFile
    ) {
      setIsSaving(true);
      try {
        // Vérif âge (info)
        const birthDate = new Date(newDateNaissance);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
        if (age < 18) {
          alert("Votre âge est susceptible de poser problème, nous étudierons votre dossier en prenant ce critère en compte");
        }

        // Uploads Storage
        const uid = user.uid;
        const photoFileRef   = ref(storage, `${uid}/photo-profil/${Date.now()}_${selectedPhotoFile.name}`);
        const kbisFileRef    = ref(storage, `${uid}/documents/kbis/${Date.now()}_${selectedKbisFile.name}`);
        const ribFileRef     = ref(storage, `${uid}/documents/rib/${Date.now()}_${selectedRibFile.name}`);
        const contratFileRef = ref(storage, `${uid}/documents/contrat/${Date.now()}_${selectedContratFile.name}`);

        await Promise.all([
          uploadBytes(photoFileRef, selectedPhotoFile),
          uploadBytes(kbisFileRef, selectedKbisFile),
          uploadBytes(ribFileRef, selectedRibFile),
          uploadBytes(contratFileRef, selectedContratFile),
        ]);

        // URLs
        const [photoURL, kbisURL, ribURL, contratURL] = await Promise.all([
          getDownloadURL(photoFileRef).catch(() => null),
          getDownloadURL(kbisFileRef).catch(() => null),
          getDownloadURL(ribFileRef).catch(() => null),
          getDownloadURL(contratFileRef).catch(() => null),
        ]);

        // Mise à jour Firestore users/{uid}
        const userDocRef = doc(firestore, 'users', uid);
        const payload = {
          pseudo: newPseudo.trim(),
          nom: newNom.trim(),
          prenom: newPrenom.trim(),
          dateNaissance: newDateNaissance,
          telephone: newTelephone.trim(),
          adresse: newAdresse.trim(),
          CGV: 'accepted',
          photoURL: photoURL || null,
          justificatifs: {
            kbisURL: kbisURL || null,
            ribURL: ribURL || null,
            contratURL: contratURL || null,
          },
          ...(newNote.trim() ? { note: newNote.trim() } : {}),
          onboardingStatus: 'submitted', // <<< sert à afficher la page d'attente
        };

        await setDoc(userDocRef, payload, { merge: true });

        // Initialiser/mettre à jour doc "code promo/{uid}"
        await handleGeneratePromoRoot(uid, newPseudo.trim());

        // On bascule sur la page d'attente (par état, et persiste via onboardingStatus)
        setStep(4);
      } catch (e) {
        console.error('Erreur sauvegarde:', e);
        alert("Une erreur est survenue lors de l'enregistrement. Veuillez réessayer.");
      } finally {
        setIsSaving(false);
      }
    } else {
      alert('Veuillez remplir tous les champs requis, importer tous les documents et accepter les CGUV.');
    }
  };

  // ====== Déconnexion
  const handleLogout = async () => {
    const auth = getAuth();
    try {
      await logoutSafely(auth);
      try {
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userRole');
      } catch {}
      navigate('/');
    } catch (e) {
      console.error('Logout error:', e);
    }
  };

  // ====== Conditions d’affichage
  const alreadySubmitted = (userDoc?.onboardingStatus === 'submitted');

  // Pré-remplir si on a déjà des données
  useEffect(() => {
    if (!userDoc) return;
    if (userDoc.pseudo && !newPseudo) setNewPseudo(userDoc.pseudo);
    if (userDoc.nom && !newNom) setNewNom(userDoc.nom);
    if (userDoc.prenom && !newPrenom) setNewPrenom(userDoc.prenom);
    if (userDoc.dateNaissance && !newDateNaissance) setNewDateNaissance(userDoc.dateNaissance);
    if (userDoc.telephone && !newTelephone) setNewTelephone(userDoc.telephone);
    if (userDoc.adresse && !newAdresse) setNewAdresse(userDoc.adresse);
    if (userDoc.CGV === 'accepted' && !acceptedCGUV) setAcceptedCGUV(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDoc]);

  // ====== Rendu
  if (!user) {
    return (
      <div className="eb-screen">
        <div className="eb-screen__panel">
          <span className="eb-spinner" aria-hidden="true" />
          <h2 className="eb-screen__title">Chargement…</h2>
          <p className="eb-screen__text">Veuillez patienter.</p>
        </div>
      </div>
    );
  }

  // ===== Page d’attente (déjà soumis OU step==4) — NOUVELLE UI "verre dépoli" + sablier
  if (alreadySubmitted || step === 4) {
    const pendingStyles = `
      .pending-wrap {
        min-height: 100vh; min-height: 100dvh;
        display: grid; place-items: center;
        padding: 32px 16px;
        background:
          radial-gradient(900px 500px at 15% -10%, rgba(39, 76, 116, 0.12), transparent 60%),
          var(--eb-bg);
        color: var(--eb-text);
      }
      .pending-card {
        width: 100%; max-width: 480px;
        background: var(--eb-surface);
        border: 1px solid var(--eb-border);
        border-radius: var(--eb-radius-xl);
        box-shadow: var(--eb-shadow-lg);
        padding: 36px 28px 28px;
        text-align: center;
      }
      .pending-title {
        margin: 0 0 6px 0;
        font-size: 22px; font-weight: 700; letter-spacing: -0.02em;
        color: var(--eb-text); text-align: center;
      }
      .pending-sub {
        margin: 0;
        color: var(--eb-muted); line-height: 1.6; font-size: 14.5px;
      }

      /* === Sablier animé === */
      .hourglass {
        display: grid; place-items: center;
        width: 88px; height: 88px; margin: 0 auto 20px;
        border-radius: 50%;
        background: var(--eb-brand-50);
        color: var(--eb-brand);
      }
      .hourglass svg { width: 48px; height: 48px; }
      .hg-rot {
        transform-origin: 24px 24px;
        animation: hg-flip 3s ease-in-out infinite; /* vitesse globale */
      }
      @keyframes hg-flip {
        0%, 10%   { transform: rotate(0deg); }       /* pause en position initiale */
        40%, 60%  { transform: rotate(180deg); }     /* pause à mi-tour */
        90%, 100% { transform: rotate(360deg); }     /* pause à la fin */
      }

      /* Actions */
      .pending-actions {
        display: flex; gap: 10px; justify-content: center; margin-top: 28px; flex-wrap: wrap;
      }
      .pending-actions .eb-btn { flex: 1 1 160px; }

      .muted {
        margin: 20px 0 0; padding-top: 16px; border-top: 1px solid var(--eb-border);
        color: var(--eb-muted); font-size: 14px; text-align: center;
      }
    `;
    return (
      <>
        <style>{pendingStyles}</style>
        <div className="pending-wrap">
          <div className="pending-card" role="status" aria-live="polite">
            {/* Sablier animé */}
            <div className="hourglass" aria-hidden="true">
              <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <g className="hg-rot">
                  {/* Contour du sablier */}
                  <path
                    d="M12 6h24a2 2 0 0 1 2 2v4c0 3.6-2 6.8-5.1 8.6l-4.9 3.4 4.9 3.4c3.1 1.8 5.1 5 5.1 8.6v4a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2v-4c0-3.6 2-6.8 5.1-8.6l4.9-3.4-4.9-3.4C12 18.8 10 15.6 10 12V8a2 2 0 0 1 2-2z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  {/* Sable haut */}
                  <path d="M19 17h10l-5 3z" fill="currentColor" opacity=".85" />
                  {/* Sable bas */}
                  <path d="M19 31h10l-5-3z" fill="currentColor" opacity=".35" />
                  {/* Filet de sable */}
                  <circle cx="24" cy="24" r="1.6" fill="currentColor" />
                </g>
              </svg>
            </div>

            <h2 className="pending-title">Votre dossier est en cours de traitement</h2>
            <p className="pending-sub">
              Nous vérifions vos informations.
            </p>

            {/* Actions */}
            <div className="pending-actions">
              <button className="eb-btn eb-btn--primary" onClick={() => window.location.reload()}>
                Actualiser
              </button>
              <button className="eb-btn eb-btn--secondary" style={{ color: 'var(--eb-danger)' }} onClick={handleLogout}>
                Déconnexion
              </button>
            </div>

            <p className="muted">Aucune action requise de votre part pour l’instant.</p>
          </div>
        </div>
      </>
    );
  }

  // ===== Formulaire multi-étapes (inchangé)
  return (
    <>
      <style>{baseStyles}</style>

      <div className="page">
        <div className="card">
          <h2 className="title">Devenez affilié Ecobill</h2>
          <p className="sub">
            Pour compléter votre profil, veuillez importez les documents requis.
          </p>

          {/* Indicateur d’étapes */}
          <div className="steps">
            <span className={`chip-ok${step >= 1 ? ' is-done' : ''}`}>
              <FaCheck aria-hidden="true" /> Étape 1 : Infos
            </span>
            <span className={`chip-ok${step >= 2 ? ' is-done' : ''}`}>
              <FaCheck aria-hidden="true" /> Étape 2 : Pièces
            </span>
            <span className={`chip-ok${step >= 3 ? ' is-done' : ''}`}>
              <FaCheck aria-hidden="true" /> Étape 3 : Note
            </span>
          </div>

          {/* Étape 1 */}
          {step === 1 && (
            <div className="section" aria-busy={isSaving}>
              <h3 className="section-title">Informations requises</h3>
              <div className="grid">
                <input
                  className="input"
                  type="text"
                  placeholder="Votre pseudo"
                  value={newPseudo}
                  onChange={(e) => setNewPseudo(e.target.value)}
                  disabled={isSaving}
                />
                <input
                  className="input"
                  type="text"
                  placeholder="Prénom"
                  value={newPrenom}
                  onChange={(e) => setNewPrenom(e.target.value)}
                  disabled={isSaving}
                />
                <input
                  className="input"
                  type="text"
                  placeholder="Nom"
                  value={newNom}
                  onChange={(e) => setNewNom(e.target.value)}
                  disabled={isSaving}
                />
                <div>
                  <label className="field-label">Date de naissance</label>
                  <input
                    className="input"
                    type="date"
                    value={newDateNaissance}
                    onChange={(e) => setNewDateNaissance(e.target.value)}
                    disabled={isSaving}
                  />
                  {newDateNaissance && (() => {
                    const birthDate = new Date(newDateNaissance);
                    const today = new Date();
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const m = today.getMonth() - birthDate.getMonth();
                    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
                    return age < 18 ? (
                      <p className="field-warn">
                        Votre âge est susceptible de poser problème, nous étudierons votre dossier en prenant ce critère en compte.
                      </p>
                    ) : null;
                  })()}
                </div>
                <input
                  className="input"
                  type="tel"
                  placeholder="Téléphone"
                  value={newTelephone}
                  onChange={(e) => setNewTelephone(e.target.value)}
                  disabled={isSaving}
                />
                <input
                  className="input"
                  type="text"
                  placeholder="Adresse complète de l'entreprise"
                  value={newAdresse}
                  onChange={(e) => setNewAdresse(e.target.value)}
                  disabled={isSaving}
                />
              </div>

              <div style={{ marginTop: 20 }}>
                <LegalAccordion
                  docId="conditions-affilies"
                  checked={acceptedCGUV}
                  onChange={setAcceptedCGUV}
                />
              </div>

              <div className="actions-row actions-row--end">
                <button
                  className="btn"
                  disabled={!isStep1Valid}
                  onClick={() => setStep(2)}
                >
                  Continuer
                </button>
              </div>
            </div>
          )}

          {/* Étape 2 */}
          {step === 2 && (
            <div className="section" aria-busy={isSaving}>
              <h3 className="section-title">Pièces justificatives</h3>

              {/* Photo de profil */}
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                {selectedPhotoFile ? (
                  <div
                    onClick={() => !isSaving && document.getElementById('photoFileInput').click()}
                    style={{ cursor: isSaving ? 'not-allowed' : 'pointer' }}
                  >
                    <img
                      src={URL.createObjectURL(selectedPhotoFile)}
                      alt="Profil"
                      style={{
                        width: 96, height: 96, borderRadius: '50%', objectFit: 'cover',
                        border: `2px solid ${THEME_PRIMARY}`, boxShadow: 'var(--eb-shadow-sm)'
                      }}
                    />
                  </div>
                ) : (
                  <div
                    onClick={() => !isSaving && document.getElementById('photoFileInput').click()}
                    style={{ cursor: isSaving ? 'not-allowed' : 'pointer' }}
                  >
                    <FaUserCircle style={{ fontSize: 96, color: 'var(--eb-brand-300)' }} />
                  </div>
                )}
                <input
                  id="photoFileInput"
                  type="file"
                  style={{ display: 'none' }}
                  onChange={(e) => setSelectedPhotoFile(e.target.files[0])}
                  accept="image/*"
                  disabled={isSaving}
                />
                {!selectedPhotoFile && (
                  <p className="field-err">
                    Importez votre photo de profil
                  </p>
                )}
              </div>

              {/* Fichiers requis */}
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${isMobile ? 1 : 3}, minmax(0, 1fr))`, gap: 12 }}>
                {/* Kbis */}
                <div
                  className={`file-tile ${selectedKbisFile ? 'ok' : ''}`}
                  onClick={() => !isSaving && document.getElementById('kbisFileInput').click()}
                  title={selectedKbisFile ? selectedKbisFile.name : undefined}
                >
                  <div>
                    <div className="file-heading">Preuve d'immatriculation</div>
                    <div className="file-sub">(Kbis, RCCM, NEQ, BCE, etc.)</div>
                  </div>
                  <div className="file-footer">
                    {selectedKbisFile ? selectedKbisFile.name : ' '}
                  </div>
                  <input
                    id="kbisFileInput"
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => setSelectedKbisFile(e.target.files[0])}
                    accept="image/*,application/pdf"
                    disabled={isSaving}
                  />
                </div>

                {/* RIB */}
                <div
                  className={`file-tile ${selectedRibFile ? 'ok' : ''}`}
                  onClick={() => !isSaving && document.getElementById('ribFileInput').click()}
                  title={selectedRibFile ? selectedRibFile.name : undefined}
                >
                  <div>
                    <div className="file-heading">RIB signé</div>
                    <div className="file-sub">JPEG / PDF</div>
                  </div>
                  <div className="file-footer">
                    {selectedRibFile ? selectedRibFile.name : ' '}
                  </div>
                  <input
                    id="ribFileInput"
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => setSelectedRibFile(e.target.files[0])}
                    accept="image/*,application/pdf"
                    disabled={isSaving}
                  />
                </div>

                {/* Contrat */}
                <div
                  className={`file-tile ${selectedContratFile ? 'ok' : ''}`}
                  onClick={() => !isSaving && document.getElementById('contratFileInput').click()}
                  title={selectedContratFile ? selectedContratFile.name : undefined}
                >
                  <div>
                    <div className="file-heading">Contrat signé</div>
                    <div className="file-sub">JPEG / PDF</div>
                  </div>
                  <div className="file-footer">
                    {selectedContratFile ? selectedContratFile.name : ' '}
                  </div>
                  <input
                    id="contratFileInput"
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => setSelectedContratFile(e.target.files[0])}
                    accept="image/*,application/pdf"
                    disabled={isSaving}
                  />
                </div>
              </div>

              <div className="actions-row">
                <button className="btn ghost" onClick={() => setStep(1)} disabled={isSaving}>Retour</button>
                <button
                  className="btn"
                  onClick={() => setStep(3)}
                  disabled={!isStep2Valid}
                >
                  Continuer
                </button>
              </div>
            </div>
          )}

          {/* Étape 3 */}
          {step === 3 && (
            <div className="section" aria-busy={isSaving}>
              <h3 className="section-title">Note facultative</h3>
              <textarea
                className="textarea"
                placeholder="Ajoutez une note (facultatif)"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                disabled={isSaving}
              />
              <div className="actions-row">
                <button className="btn ghost" onClick={() => setStep(2)} disabled={isSaving}>Retour</button>
                <button className="btn secondary" onClick={handleSaveAll} disabled={isSaving}>
                  {isSaving && <span className="eb-spinner eb-spinner--sm eb-spinner--light" aria-hidden="true" />}
                  {isSaving ? 'Enregistrement…' : 'Envoyer mon dossier'}
                </button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--eb-border)' }}>
            <button
              className="eb-btn eb-btn--secondary"
              style={{ color: 'var(--eb-danger)' }}
              onClick={handleLogout}
            >
              Déconnexion
            </button>
          </div>

        </div>
      </div>
    </>
  );
};

export default AffiliatesPending;
